import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { adminAuthHeader } from "@/hooks/useAdminAuth";
import { extractApiError } from "@/utils/apiError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Palette of CRM-style row colors. Order matters (renders left → right).
 * The key is sent to the backend; the value is the visible swatch + the
 * pastel background used by the row when this color is active.
 */
export const ROW_COLORS = {
  red:    { dot: "#dc2626", bg: "#fee2e2", border: "#fecaca" },
  orange: { dot: "#ea580c", bg: "#ffedd5", border: "#fed7aa" },
  yellow: { dot: "#ca8a04", bg: "#fef9c3", border: "#fde68a" },
  green:  { dot: "#16a34a", bg: "#dcfce7", border: "#bbf7d0" },
  blue:   { dot: "#2563eb", bg: "#dbeafe", border: "#bfdbfe" },
  purple: { dot: "#9333ea", bg: "#f3e8ff", border: "#e9d5ff" },
  pink:   { dot: "#db2777", bg: "#fce7f3", border: "#fbcfe8" },
  gray:   { dot: "#475569", bg: "#e2e8f0", border: "#cbd5e1" },
  white:  { dot: "#ffffff", bg: "#ffffff", border: "#cbd5e1" },
};

export function rowBgForColor(color) {
  if (!color) return undefined;
  const c = ROW_COLORS[color];
  return c ? c.bg : undefined;
}

/**
 * Small color dot that opens a popover with the palette. Click a swatch to
 * persist via PATCH /admin/users/{id}/meta. Click X to clear.
 */
export default function UserRowColorPicker({ userId, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const save = async (newColor) => {
    setBusy(true);
    try {
      await axios.patch(
        `${API}/admin/users/${userId}/meta`,
        { color: newColor },
        { headers: adminAuthHeader() }
      );
      onChange?.(newColor);
      setOpen(false);
      toast.success(newColor ? "Colore aggiornato" : "Colore rimosso");
    } catch (err) {
      const detail = extractApiError(err, "Errore");
      toast.error(`Impossibile salvare il colore: ${detail}`);
    } finally {
      setBusy(false);
    }
  };

  const currentDot = value && ROW_COLORS[value] ? ROW_COLORS[value].dot : "transparent";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`user-color-trigger-${userId}`}
          aria-label="Cambia colore riga"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center justify-center w-6 h-6 rounded-full border transition hover:scale-110"
          style={{
            background: value ? currentDot : "#ffffff",
            borderColor: value ? currentDot : "#cbd5e1",
            boxShadow: value ? "0 1px 3px rgba(0,0,0,0.15)" : "inset 0 0 0 1px #e2e8f0",
          }}
        />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto p-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-1.5" data-testid={`user-color-palette-${userId}`}>
          {Object.entries(ROW_COLORS).map(([key, { dot }]) => (
            <button
              key={key}
              type="button"
              data-testid={`user-color-${key}-${userId}`}
              disabled={busy}
              onClick={() => save(key)}
              className="relative inline-flex items-center justify-center w-7 h-7 rounded-full hover:scale-110 transition disabled:opacity-50"
              style={{ background: dot, border: key === "white" ? "1px solid #cbd5e1" : "none" }}
              title={key}
            >
              {value === key && <Check size={14} color={key === "white" || key === "yellow" ? "#0f172a" : "#ffffff"} strokeWidth={3} />}
            </button>
          ))}
          <button
            type="button"
            data-testid={`user-color-clear-${userId}`}
            disabled={busy || !value}
            onClick={() => save(null)}
            className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-slate-300 hover:bg-slate-50 transition disabled:opacity-40"
            title="Rimuovi colore"
          >
            <X size={14} color="#475569" />
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
